import React, { useCallback } from 'react';
import _ from 'lodash';
import {
  Button,
  Table,
  Row,
  Col,
  FormGroup,
  Label,
  Collapse,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import {
  NumberInput,
  TextInput,
  BooleanInput,
} from 'flaivy-react/components/inputs';

const FilterpanelDefaultFilters = ({ onChange, articleFilterPanelConfig }) => {
  return (
    <Row>
      <Col xs={12}>
        <h4>Man ska kunna filtrera på</h4>
      </Col>
      <Col md={4}>
        <FormGroup>
          {/* eslint-disable-next-line */}

          <Label>Kategorier</Label>

          <BooleanInput
            name="useProductCategoriesFacet"
            value={!!articleFilterPanelConfig.useProductCategoriesFacet}
            onChange={onChange}
          />
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup>
          {/* eslint-disable-next-line */}

          <Label>Leveransalternativ</Label>

          <BooleanInput
            name="useResponsibleForDeliveryFacet"
            value={!!articleFilterPanelConfig.useResponsibleForDeliveryFacet}
            onChange={onChange}
          />
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup>
          {/* eslint-disable-next-line */}

          <Label>Kräver alkoholtillstånd</Label>

          <BooleanInput
            name="useRequiresAlcoholFacet"
            value={!!articleFilterPanelConfig.useRequiresAlcoholFacet}
            onChange={onChange}
          />
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup>
          {/* eslint-disable-next-line */}

          <Label>Ekologiskt</Label>

          <BooleanInput
            name="useEcoFacet"
            value={!!articleFilterPanelConfig.useEcoFacet}
            onChange={onChange}
          />
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup>
          {/* eslint-disable-next-line */}

          <Label>Kampanj</Label>

          <BooleanInput
            name="useCampaignFacet"
            value={!!articleFilterPanelConfig.useCampaignFacet}
            onChange={onChange}
          />
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup>
          {/* eslint-disable-next-line */}

          <Label>Ursprung</Label>

          <BooleanInput
            name="useOriginFacet"
            value={!!articleFilterPanelConfig.useOriginFacet}
            onChange={onChange}
          />
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup>
          {/* eslint-disable-next-line */}

          <Label>Förpackning</Label>

          <BooleanInput
            name="useContainerFacet"
            value={!!articleFilterPanelConfig.useContainerFacet}
            onChange={onChange}
          />
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup>
          {/* eslint-disable-next-line */}

          <Label>Tillredningsform</Label>

          <BooleanInput
            name="usePreparationFormatFacet"
            value={!!articleFilterPanelConfig.usePreparationFormatFacet}
            onChange={onChange}
          />
        </FormGroup>
      </Col>
    </Row>
  );
};

function TableRow({ shortcut, onChange, removeShortcuts }) {
  const handleChange = (key, value) => onChange({ ...shortcut, [key]: value });

  return (
    <tr id={shortcut.id}>
      <td>
        <TextInput
          name="name"
          id="nameField"
          value={shortcut.name}
          onChange={handleChange}
        />
      </td>
      <td>
        <TextInput
          name="link"
          id="link"
          value={shortcut.link}
          onChange={handleChange}
        />
      </td>
      <td>
        <NumberInput
          name="sortOrder"
          id={`sortOrderField-${shortcut.id}`}
          value={shortcut.sortOrder}
          onChange={handleChange}
          validationError="Ej giltig siffra"
        />
      </td>
      <td>
        <BooleanInput
          name="openInNewTab"
          id={`openInNewTab-${shortcut.id}`}
          value={shortcut.openInNewTab}
          onChange={handleChange}
        />
      </td>

      <td>
        <Button
          color="secondary"
          className="float-end m-0"
          onClick={() => removeShortcuts(shortcut.id)}
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </td>
    </tr>
  );
}

function ArticleFilterConfigEditor({ value, onChange, parentEntity }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const shortcuts = _.get(value, 'shortcuts', []);

  const addShortcut = () => {
    const id = _.uuid();

    onChange('articleFilterPanelConfig', {
      ...value,
      shortcuts: [...shortcuts, { id, sortOrder: shortcuts.length + 1 }],
    });
  };

  const removeShortcuts = useCallback(
    id => {
      return onChange('articleFilterPanelConfig', {
        ...value,
        shortcuts: shortcuts.filter(shortcut => shortcut.id !== id),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, onChange],
  );

  const handleChange = (key, newValue) => {
    onChange('articleFilterPanelConfig', { ...value, [key]: newValue });
  };

  const handleShortcutRowChange = row => {
    const indexOfShortCutToUpdate = _.findIndex(
      shortcuts,
      shortcut => shortcut.id === row.id,
    );
    const updatedShortcut = [
      ...shortcuts.slice(0, indexOfShortCutToUpdate),
      row,
      ...shortcuts.slice(indexOfShortCutToUpdate + 1),
    ];
    const sortedShortcuts = updatedShortcut.sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );

    handleChange('shortcuts', sortedShortcuts);
  };

  return (
    <div>
      <Button
        color="primary"
        onClick={() => setIsOpen(!isOpen)}
        style={{ marginBottom: '1rem' }}
      >
        Visa filterpanelkonfiguration
      </Button>
      <Collapse isOpen={isOpen}>
        <Row>
          <Col xs={12}>
            <h5>Kategorier</h5>
          </Col>
          <Col md={4}>
            <FormGroup>
              {/* eslint-disable-next-line */}

              <Label>
                Kategorier är en navigationsmeny istället för en filterpanel
              </Label>

              <BooleanInput
                name="useNavigationInsteadOfFilter"
                value={!!value.useNavigationInsteadOfFilter}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>

          {value.useNavigationInsteadOfFilter && (
            <Col md={4}>
              <FormGroup>
                {/* eslint-disable-next-line */}

                <Label>
                  Egna länkar är del av navigationsmenyn istället för fristående
                </Label>

                <BooleanInput
                  name="integrateShortcutsInCategories"
                  value={!!value.integrateShortcutsInCategories}
                  onChange={handleChange}
                />
              </FormGroup>
            </Col>
          )}
        </Row>
        <h5>Egna länkar</h5>
        {!value.integrateShortcutsInCategories && (
          <>
            <Label>Överskrift</Label>
            <TextInput
              name="name"
              id="nameField"
              value={value.name || 'Genvägar'}
              onChange={handleChange}
            />
          </>
        )}
        <Table className="px-0">
          <thead>
            <tr>
              <th>Namn</th>
              <th>Länk</th>
              <th>Sorteringsordning</th>
              <th>Öppna i ny flik</th>
            </tr>
          </thead>
          <tbody>
            {shortcuts.map(shortcut => {
              const { id } = shortcut;
              return (
                <TableRow
                  shortcut={shortcut}
                  removeShortcuts={removeShortcuts}
                  onChange={handleShortcutRowChange}
                  key={id}
                />
              );
            })}
          </tbody>
        </Table>
        <Button className="mb-3" onClick={addShortcut}>
          +
        </Button>
        <FilterpanelDefaultFilters
          onChange={handleChange}
          articleFilterPanelConfig={value}
        />
      </Collapse>
    </div>
  );
}

ArticleFilterConfigEditor.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

ArticleFilterConfigEditor.defaultProps = {
  value: { shortcuts: [] },
};

export default ArticleFilterConfigEditor;
